package fr.frux.core.maps

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import kotlin.js.json

/**
 * Le navigateur gere lui-meme la boite de dialogue : la seule facon de la faire surgir est
 * d'appeler `getCurrentPosition` (voir [LocationPermissionController.request]). Un refus est
 * definitif comme sur iOS — redemander par code n'affiche plus rien, seuls les reglages de site
 * le permettent — d'ou `denied` → PERMANENTLY_DENIED, jamais DENIED simple.
 *
 * `promptOnLaunch` est faux : Chrome penalise (voire bloque) les prompts hors geste utilisateur,
 * la demande attend le premier clic sur « ma position ». `canOpenAppSettings` est faux : les
 * reglages de site ne sont pas ouvrables par script.
 */
@Composable
actual fun rememberLocationPermissionController(): LocationPermissionController {
    // Un accord deja constate dans cette session (#464, iOS Safari ne rapporte jamais `granted`)
    // vaut pour tout nouveau controleur : l'accueil l'a obtenu, la carte doit le voir (#502).
    val statusState = remember { mutableStateOf(initialStatusFromSession()) }

    DisposableEffect(Unit) {
        var permissionStatus: dynamic = null
        val permissions: dynamic = js("navigator.permissions ? navigator.permissions : null")
        if (permissions != null) {
            permissions.query(json("name" to "geolocation")).then(
                { status: dynamic ->
                    permissionStatus = status
                    statusState.value = (status.state as? String).toPermissionStatus().orSessionGrant()
                    status.onchange = {
                        statusState.value = (status.state as? String).toPermissionStatus().orSessionGrant()
                    }
                },
                { _: dynamic -> Unit },
            )
        }
        onDispose {
            if (permissionStatus != null) permissionStatus.onchange = null
        }
    }

    return remember {
        object : LocationPermissionController {
            override val status: LocationPermissionStatus
                get() = statusState.value

            override val shouldExplain: Boolean = false

            override val promptOnLaunch: Boolean = false

            override val canOpenAppSettings: Boolean = false

            override fun request() {
                // Seul moyen web de declencher la popup de permission. Le resultat remonte par
                // ces callbacks, pas par l'onchange de la Permissions API : sur iOS l'etat reste
                // « prompt » apres l'accord et onchange ne vient jamais (#464). Une position
                // rendue vaut accord pour la session (cf. geolocationAccess) et rechauffe en prime
                // le cache de position (maximumAge du provider). Un echec autre qu'un refus (pas
                // de fix, delai) vient apres l'accord de l'utilisateur : il compte comme un accord,
                // le provider rendra Unavailable a son tour et l'ecran l'affichera.
                browserGeolocation()?.getCurrentPosition(
                    { _ ->
                        geolocationGrantObserved = true
                        statusState.value = LocationPermissionStatus.GRANTED
                    },
                    { error ->
                        if (error.code == ERROR_PERMISSION_DENIED) {
                            statusState.value = LocationPermissionStatus.PERMANENTLY_DENIED
                        } else {
                            geolocationGrantObserved = true
                            statusState.value = LocationPermissionStatus.GRANTED
                        }
                    },
                    json(),
                )
            }

            // Jamais atteint : canOpenAppSettings est faux, le bandeau n'offre pas de bouton.
            override fun openAppSettings() = Unit
        }
    }
}

private fun String?.toPermissionStatus(): LocationPermissionStatus =
    when (this) {
        "granted" -> LocationPermissionStatus.GRANTED
        "denied" -> LocationPermissionStatus.PERMANENTLY_DENIED
        else -> LocationPermissionStatus.NOT_REQUESTED
    }

private fun initialStatusFromSession(): LocationPermissionStatus =
    if (geolocationGrantObserved) LocationPermissionStatus.GRANTED else LocationPermissionStatus.NOT_REQUESTED

/** Un « prompt » de la Permissions API ne retrograde pas un accord deja constate dans la session. */
private fun LocationPermissionStatus.orSessionGrant(): LocationPermissionStatus =
    if (this == LocationPermissionStatus.NOT_REQUESTED && geolocationGrantObserved) {
        LocationPermissionStatus.GRANTED
    } else {
        this
    }
